import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function B2BPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    company: user?.company || "",
    contact_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    items: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/b2b/quotes/mine");
        setQuotes(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const items = form.items
        .split("\n")
        .filter((l) => l.trim())
        .map((line) => ({ description: line.trim() }));
      await api.post("/b2b/quote", {
        company: form.company,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone || null,
        items,
        notes: form.notes || null,
      });
      toast.success("Demande envoyée", { description: "Notre équipe revient vers vous sous 24h." });
      setForm({ ...form, items: "", notes: "", phone: "" });
      const { data } = await api.get("/b2b/quotes/mine");
      setQuotes(data);
    } catch (e) {
      toast.error("Erreur", { description: e.response?.data?.detail || "Impossible d'envoyer" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="b2b-page">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Espace Pro</div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none">
        B2B — Tarifs entreprise
      </h1>
      <p className="mt-4 max-w-2xl text-sm text-brand-gray font-roboto">
        Vous équipez vos collaborateurs ? Demandez un devis personnalisé.
        Tarifs dégressifs dès 5 unités, leasing possible, garantie étendue.
      </p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 border border-brand-blue/40 rounded-sm p-6 space-y-4" data-testid="b2b-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Entreprise" testId="b2b-company" value={form.company} onChange={update("company")} required />
            <Field label="Contact" testId="b2b-contact-name" value={form.contact_name} onChange={update("contact_name")} required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="E-mail" type="email" testId="b2b-email" value={form.email} onChange={update("email")} required />
            <Field label="Téléphone" testId="b2b-phone" value={form.phone} onChange={update("phone")} />
          </div>
          <Textarea
            label="Articles souhaités (un par ligne)"
            testId="b2b-items"
            value={form.items}
            onChange={update("items")}
            rows={5}
            placeholder={"10× MacBook Pro 14\"\n5× iPad Pro 11\""}
            required
          />
          <Textarea label="Notes complémentaires" testId="b2b-notes" value={form.notes} onChange={update("notes")} rows={3} />
          <button
            type="submit"
            disabled={submitting}
            data-testid="b2b-submit-button"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-sm bg-brand-green text-black font-bold hover:bg-brand-green/90 disabled:opacity-50 transition shadow-glow-green"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Demander un devis</>}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-3" data-testid="b2b-quotes-list">
          <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green">Mes demandes</h2>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
          ) : quotes.length === 0 ? (
            <p className="text-sm text-brand-gray/70 font-roboto">Aucune demande pour l'instant.</p>
          ) : (
            quotes.map((q) => (
              <div key={q.id} className="border border-brand-blue/40 rounded-sm p-4 text-sm" data-testid={`b2b-quote-${q.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white">{q.company}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-brand-green font-bold inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> {q.status}
                  </span>
                </div>
                <div className="text-xs text-brand-gray/60 font-roboto mt-1">
                  {new Date(q.created_at).toLocaleString("fr-FR")} · {q.items?.length || 0} ligne(s)
                </div>
              </div>
            ))
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gray mb-2">{label}</span>
      <input {...props} data-testid={testId} className="w-full h-11 px-4 bg-black border border-brand-blue/60 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-green font-roboto" />
    </label>
  );
}
function Textarea({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gray mb-2">{label}</span>
      <textarea {...props} data-testid={testId} className="w-full px-4 py-3 bg-black border border-brand-blue/60 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-green font-roboto resize-y" />
    </label>
  );
}
